/**
 * @fileoverview Fires `experiment_exposure` once per session per eligible experiment.
 * @description Surface-aware, consent-gated dual write to GA4/Vercel and Postgres.
 */

"use client"

import { usePathname } from "next/navigation"
import { useEffect, useRef, useState } from "react"

import { track } from "~/lib/analytics"
import {
  CONSENT_CHANGED_EVENT,
  CONSENT_STORAGE_KEY,
  isAnalyticsAllowed,
} from "~/lib/analytics/consent"
import { isAnalyticsStorageAllowedAsync } from "~/lib/analytics/consent-storage-async"
import {
  clearActiveExperiments,
  hasAssignmentsBootstrap,
  persistActiveExperiments,
  readBootstrappedAssignments,
  readFreshLedgerIngestToken,
} from "~/lib/experiments/client-dimensions"
import {
  filterAssignmentsForPath,
  type ExperimentAssignment,
} from "~/lib/experiments/dimensions"
import { LEDGER_INGEST_TOKEN_HEADER } from "~/lib/experiments/ledger-ingest-client"
import { useExperimentAssignments } from "~/lib/experiments/exposure-context"
import { surfaceLabel } from "~/lib/experiments/surfaces"

/** GA exposure-ping dedupe (written when the ping fires). */
const STORAGE_PREFIX = "adsights_exp_exposed:"
/** Ledger-row COMPLETION marker — written only after a 2xx ingest response. */
const LEDGER_STORAGE_PREFIX = "adsights_exp_ledger:"

/**
 * Session dedupe key for an exposure (GA ping + ledger POST).
 *
 * The dedupe scope MUST match how the Postgres ledger dedupes so the GA4 and
 * ledger exposed denominators agree. The ledger dedupes per
 * `(experiment_key, variant_id, visitor_id)` (migration 0012) — i.e. once per
 * visitor, path-agnostic. For a `path`/`path_prefix` surface the experiment
 * lives on a single route, so keying the client dedupe on `pathname` already
 * yields one exposure per session. But a `global` surface fires on EVERY route,
 * so keying on `pathname` would emit one GA `experiment_exposure` per visited
 * route — inflating the GA4 denominator against the ledger's one-per-visitor
 * count (they then disagree in any GA4/ledger blend). Global experiments
 * therefore dedupe WITHOUT pathname (once per session), matching the ledger.
 *
 * @param {ExperimentAssignment} assignment - The eligible assignment.
 * @param {string} pathname - Current route.
 * @returns {string} Dedupe key.
 */
function exposureDedupeKey(
  assignment: ExperimentAssignment,
  pathname: string,
): string {
  const scope = assignment.surface.type === "global" ? "*" : pathname
  return `${assignment.experiment_id}:${assignment.variant_id}:${scope}`
}

/**
 * Removes every exposure-dedupe key — both the GA-ping keys and the
 * ledger-completion keys (always permitted — deletion needs no consent).
 * The accessor itself can throw under cookie-blocking privacy policies, so
 * the whole sweep sits in one try.
 *
 * @returns {void}
 */
function clearExposureDedupeKeys(): void {
  try {
    const staleKeys: string[] = []
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i)
      if (
        key?.startsWith(STORAGE_PREFIX) ||
        key?.startsWith(LEDGER_STORAGE_PREFIX)
      ) {
        staleKeys.push(key)
      }
    }
    for (const key of staleKeys) {
      sessionStorage.removeItem(key)
    }
  } catch {
    // Storage may be blocked.
  }
}

/**
 * Records surface-eligible experiment exposures after consent.
 *
 * @returns {null} Renderless analytics side-effect component.
 */
export function ExperimentExposureBeacon(): null {
  const contextAssignments = useExperimentAssignments()
  const pathname = usePathname()
  const firedRef = useRef<Set<string>>(new Set())
  // Ledger writes are deduped separately from the GA ping: a pre-consent
  // visitor may fire the (cookieless) GA exposure first and only become
  // ledger-eligible after opting in — the consent re-run must still be able
  // to record the ledger row even though the GA fire is already deduped.
  const ledgerWrittenRef = useRef<Set<string>>(new Set())
  const [consentTick, setConsentTick] = useState(0)

  useEffect(() => {
    const onConsentChanged = (): void => {
      setConsentTick((value) => value + 1)
    }
    // Cross-tab withdrawals arrive as a `storage` event for the consent
    // key, NOT the same-tab custom event — without this listener an
    // opt-out in another tab left this tab's dedupe refs and persisted
    // dimensions alive, so an opt-back-in here could reuse pre-opt-out
    // state (#233 `r3519603527`). `key === null` means storage.clear().
    const onStorage = (event: StorageEvent): void => {
      if (event.key === null || event.key === CONSENT_STORAGE_KEY) {
        setConsentTick((value) => value + 1)
      }
    }
    window.addEventListener(CONSENT_CHANGED_EVENT, onConsentChanged)
    window.addEventListener("storage", onStorage)
    return () => {
      window.removeEventListener(CONSENT_CHANGED_EVENT, onConsentChanged)
      window.removeEventListener("storage", onStorage)
    }
  }, [])

  useEffect(() => {
    if (!isAnalyticsAllowed()) {
      // Explicit opt-out: purge experiment session state instead of letting it
      // survive the opt-out — otherwise an opt-back-in within the same tab
      // session would suppress a fresh exposure via the dedupe keys while
      // reusing the pre-opt-out assignment dims (#233 `r3516809932`). This
      // fires on an EXPLICIT analytics opt-out (the visitor's own choice), not
      // on GPC — GPC is a sale/share opt-out and no longer denies first-party
      // analytics. In-memory refs reset too so a re-grant re-fires cleanly.
      clearActiveExperiments()
      clearExposureDedupeKeys()
      firedRef.current.clear()
      ledgerWrittenRef.current.clear()
      return
    }

    const assignments =
      contextAssignments.length > 0
        ? contextAssignments
        : readBootstrappedAssignments()

    if (assignments.length === 0) {
      // Clear stale persisted assignments ONLY on an authoritative empty —
      // i.e. the bootstrap fetch resolved (or a legacy DOM bootstrap exists)
      // and reported nothing running (#233 `r3515451596`). Before the fetch
      // resolves, empty just means "not loaded yet"; clearing then would
      // break cross-page conversion attribution. Deleting needs no consent.
      if (hasAssignmentsBootstrap()) {
        clearActiveExperiments()
      }
      return
    }

    const eligible = filterAssignmentsForPath(assignments, pathname)

    // PHASE 1 (sync) — GA exposure pings. Allowed pre-opt-in in
    // consent-required regions (Consent Mode sends them cookieless), so they
    // fire immediately rather than waiting on the region gate below.
    for (const assignment of eligible) {
      const dedupeKey = exposureDedupeKey(assignment, pathname)

      const storageKey = `${STORAGE_PREFIX}${dedupeKey}`
      // Cross-session dedup via sessionStorage, but DON'T let a storage failure
      // (disabled / private-mode) skip the exposure: isolate the read so we fall
      // back to the in-memory `firedRef` instead of dropping the visitor out of
      // the exposed denominator entirely (#233 r3417619312). Reading an
      // existing key is fine pre-consent; only WRITES are storage-gated.
      let alreadyFiredThisSession = false
      try {
        alreadyFiredThisSession = sessionStorage.getItem(storageKey) !== null
      } catch {
        // sessionStorage unavailable — rely on firedRef for in-memory dedup.
      }
      if (alreadyFiredThisSession) {
        // GA dedupe ONLY — ledger completion has its own key
        // (`LEDGER_STORAGE_PREFIX`), set strictly after a 2xx response, so a
        // prior page load whose POST failed still retries below
        // (#233 `r3517094418`).
        firedRef.current.add(dedupeKey)
        continue
      }

      try {
        if (!firedRef.current.has(dedupeKey)) {
          track("experiment_exposure", {
            experiment_id: assignment.experiment_id,
            variant_id: assignment.variant_id,
            experiment_kind: assignment.kind,
            surface_path: pathname,
            surface_scope: surfaceLabel(assignment.surface),
          })
          firedRef.current.add(dedupeKey)
        }
      } catch {
        // Analytics must never break UX.
      }
    }

    // PHASE 2 (async) — everything that WRITES first-party analytics state:
    // the sessionStorage dedupe/dimension keys and the visitor-scoped
    // Postgres ledger rows need the stricter storage gate, exactly like the
    // attribution layer (#233 `r3515451592`) — and the REGION-AWARE variant
    // of it: when middleware didn't stamp the region cookie (cacheable hop,
    // blocked Set-Cookie), the sync gate's rest-of-world default would let a
    // pre-consent EEA/UK/CH visitor's rows through. Hydrating first mirrors
    // `recordAttributionTouchAsync` from the Consent Mode v2 reach fix
    // (#479); ledger rows can't be purged retroactively, so they must not be
    // written on an unconfirmed region default.
    //
    // Deliberately NOT cancelled on unmount (#233 `r3516720328`): these are
    // analytics writes, not React state updates — a visitor who clicks
    // through to /request-access before region hydration resolves must still
    // get their exposure persisted and their ledger row recorded (keepalive
    // fetch survives navigation), or the funnel events lose their experiment
    // context and the ledger under-counts the exposed denominator. The gate
    // reads CURRENT consent state when it resolves, so completing late never
    // bypasses a consent change, and the dedupe refs make re-runs idempotent.
    void (async () => {
      let storageAllowed = false
      try {
        storageAllowed = await isAnalyticsStorageAllowedAsync()
      } catch {
        // Treat a gate failure as not-allowed; the consent-change re-run
        // (or next mount) retries.
      }

      if (!storageAllowed) {
        // Pre-consent in a consent-required region: never persist, and drop
        // anything a previous consented session left behind. The GA pings
        // above already fired cookieless; the consent-change re-run records
        // the ledger writes if the visitor opts in during this mount.
        clearActiveExperiments()
        return
      }

      persistActiveExperiments(assignments, pathname)
      // Fresh-or-refresh: a consent-delayed exposure in a tab open past the
      // token's 24h TTL must not POST the stale credential (#233 `r3517282446`).
      const ingestToken = await readFreshLedgerIngestToken()

      // Re-check AFTER the await: a same-tab opt-out during a slow token
      // refresh must not slip dedupe writes or ledger POSTs past the gate
      // that was true when the phase started (#233 `r3519603532`). The
      // opt-out cleanup itself runs via the consent-change re-run.
      try {
        if (!(await isAnalyticsStorageAllowedAsync())) {
          return
        }
      } catch {
        return
      }

      for (const assignment of eligible) {
        const dedupeKey = exposureDedupeKey(assignment, pathname)
        const storageKey = `${STORAGE_PREFIX}${dedupeKey}`
        const ledgerKey = `${LEDGER_STORAGE_PREFIX}${dedupeKey}`

        try {
          try {
            sessionStorage.setItem(storageKey, "1")
          } catch {
            // sessionStorage unavailable — firedRef already prevents a repeat this mount.
          }

          // Ledger dedupe is SUCCESS state, not attempt state: `fetch` does
          // not reject on HTTP errors, so marking before/regardless of the
          // response turned any transient 403/503 (or dropped keepalive) into
          // a permanently missing exposure row for the tab session — and the
          // ledger is authoritative over GA4 once it has rows, so that
          // undercounts the variant's denominator (#233 `r3517094418`). The
          // durable key is written ONLY on a 2xx; the in-memory ref guards
          // same-mount duplicates while in flight and is rolled back on
          // failure so the next effect run / page load retries. Retries are
          // server-safe: exposure inserts dedupe per visitor
          // (ON CONFLICT DO NOTHING, migration 0012).
          let ledgerDone = false
          try {
            ledgerDone = sessionStorage.getItem(ledgerKey) !== null
          } catch {
            // Storage unavailable — rely on the in-memory ref.
          }
          if (ledgerDone || ledgerWrittenRef.current.has(dedupeKey)) {
            continue
          }
          ledgerWrittenRef.current.add(dedupeKey)

          const headers: Record<string, string> = {
            "Content-Type": "application/json",
          }
          if (ingestToken) {
            headers[LEDGER_INGEST_TOKEN_HEADER] = ingestToken
          }

          void fetch("/api/experiments/exposure", {
            method: "POST",
            headers,
            body: JSON.stringify({
              experiment_id: assignment.experiment_id,
              variant_id: assignment.variant_id,
              surface_path: pathname,
              experiment_kind: assignment.kind,
            }),
            keepalive: true,
          })
            .then((response) => {
              if (!response.ok) {
                ledgerWrittenRef.current.delete(dedupeKey)
                return
              }
              try {
                sessionStorage.setItem(ledgerKey, "1")
              } catch {
                // Storage unavailable — the ref keeps this mount deduped;
                // a later page load may retry, which the server dedupes.
              }
            })
            .catch(() => {
              // Dropped/failed request — roll back so a re-run retries.
              ledgerWrittenRef.current.delete(dedupeKey)
            })
        } catch {
          // Analytics must never break UX.
        }
      }
    })()
  }, [contextAssignments, pathname, consentTick])

  return null
}
